import { BaseMessageSignerWalletAdapter, BaseWalletAdapter, WalletReadyState } from '@solana/wallet-adapter-base';
import { LedgerWalletAdapter } from '@solana/wallet-adapter-ledger';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { WalletConnectWalletAdapter } from '@solana/wallet-adapter-walletconnect';
import { PublicKey } from '@solana/web3.js';
import {
    backpackBrowseUrl,
    binanceBrowseUrl,
    getBinanceSolanaProvider,
    isInBinance,
    isInPhantom,
    isInSolflare,
    isInWalletBrowser,
    isProbablyMobile,
    phantomBrowseUrl,
    solflareBrowseUrl,
    trustBrowseUrl
} from '../utils/mobileWallet';

// Official Binance Web3 Wallet icon (WalletConnect registry id: com.binance.wallet).
const BINANCE_ICON = `${process.env.PUBLIC_URL || ''}/assets/binance-wallet.webp`;

export const createWalletConnectAdapter = (network) => {
    const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '';
    if (!projectId) {
        return null;
    }
    return new WalletConnectWalletAdapter({
        network,
        options: {
            relayUrl: 'wss://relay.walletconnect.com',
            projectId,
            metadata: {
                name: 'Flash SOL to ETH Migration',
                description: 'Migrate FLASH from Solana to Ethereum',
                url: 'https://migration.flash-technologies.org',
                icons: ['https://migration.flash-technologies.org/logo192.png']
            }
        }
    });
};

const shouldRedirectToWalletBrowser = (isInsideWallet) => (
    isProbablyMobile() && !isInsideWallet()
);

export class MobilePhantomWalletAdapter extends PhantomWalletAdapter {
    get readyState() {
        if (shouldRedirectToWalletBrowser(isInPhantom)) {
            return WalletReadyState.Loadable;
        }
        return super.readyState;
    }

    async autoConnect() {
        // Never bounce to Phantom on page load; wait for an explicit tap.
        if (shouldRedirectToWalletBrowser(isInPhantom)) {
            return;
        }
        return super.autoConnect();
    }

    async connect() {
        if (shouldRedirectToWalletBrowser(isInPhantom)) {
            window.location.href = phantomBrowseUrl();
            return;
        }
        return super.connect();
    }
}

export class MobileSolflareWalletAdapter extends SolflareWalletAdapter {
    async autoConnect() {
        if (shouldRedirectToWalletBrowser(isInSolflare)) {
            return;
        }
        return super.autoConnect();
    }

    async connect() {
        if (shouldRedirectToWalletBrowser(isInSolflare)) {
            window.location.href = solflareBrowseUrl();
            return;
        }
        return super.connect();
    }
}

class InAppBrowserWalletAdapter extends BaseWalletAdapter {
    constructor({ name, url, icon, buildBrowseUrl }) {
        super();
        this.name = name;
        this.url = url;
        this.icon = icon;
        this.buildBrowseUrl = buildBrowseUrl;
        this.supportedTransactionVersions = null;
        if (typeof window !== 'undefined') {
            queueMicrotask(() => this.emit('readyStateChange', this.readyState));
        }
    }

    get publicKey() {
        return null;
    }

    get connecting() {
        return false;
    }

    get readyState() {
        if (typeof window === 'undefined') {
            return WalletReadyState.Unsupported;
        }
        if (isInWalletBrowser()) {
            return WalletReadyState.NotDetected;
        }
        return isProbablyMobile() ? WalletReadyState.Loadable : WalletReadyState.NotDetected;
    }

    async autoConnect() {
        return;
    }

    async connect() {
        window.location.href = this.buildBrowseUrl();
    }

    async disconnect() {
        return;
    }

    async sendTransaction() {
        throw new Error('Open this site inside your Solana wallet browser to continue');
    }
}

export class BinanceWalletAdapter extends BaseMessageSignerWalletAdapter {
    constructor() {
        super();
        this.name = 'Binance';
        this.url = 'https://www.binance.com/en/web3wallet';
        this.icon = BINANCE_ICON;
        this.supportedTransactionVersions = new Set(['legacy']);
        this._connecting = false;
        this._publicKey = null;
        this._provider = null;
        if (typeof window !== 'undefined') {
            queueMicrotask(() => this.emit('readyStateChange', this.readyState));
        }
    }

    get publicKey() {
        return this._publicKey;
    }

    get connecting() {
        return this._connecting;
    }

    get readyState() {
        if (typeof window === 'undefined') {
            return WalletReadyState.Unsupported;
        }
        if (getBinanceSolanaProvider()) {
            return WalletReadyState.Installed;
        }
        if (isInBinance()) {
            return WalletReadyState.Loadable;
        }
        if (isInWalletBrowser()) {
            return WalletReadyState.NotDetected;
        }
        return WalletReadyState.Loadable;
    }

    async autoConnect() {
        if (!getBinanceSolanaProvider()) {
            return;
        }
        return this.connect();
    }

    async connect() {
        if (this.connected || this._connecting) {
            return;
        }

        const provider = getBinanceSolanaProvider();
        if (!provider) {
            if (isInBinance()) {
                throw new Error('Enable Solana in Binance Web3 → More → Networks, then connect again.');
            }
            window.location.href = binanceBrowseUrl();
            return;
        }

        this._connecting = true;
        try {
            const response = await provider.connect();
            const rawKey = response?.publicKey || provider.publicKey;
            if (!rawKey) {
                throw new Error('Binance Wallet did not return a Solana address');
            }
            this._provider = provider;
            this._publicKey = new PublicKey(rawKey.toString());
            this.emit('connect', this._publicKey);
        } finally {
            this._connecting = false;
        }
    }

    async disconnect() {
        const provider = this._provider;
        this._provider = null;
        this._publicKey = null;
        if (provider && typeof provider.disconnect === 'function') {
            await provider.disconnect();
        }
        this.emit('disconnect');
    }

    async signMessage(message) {
        if (!this._provider || typeof this._provider.signMessage !== 'function') {
            throw new Error('Binance Wallet cannot sign this migration message');
        }
        const signed = await this._provider.signMessage(message);
        if (signed instanceof Uint8Array) {
            return signed;
        }
        if (signed?.signature) {
            return signed.signature instanceof Uint8Array
                ? signed.signature
                : Uint8Array.from(signed.signature);
        }
        throw new Error('Binance Wallet returned an unsupported signature format');
    }

    async signTransaction(transaction) {
        if (!this._provider || typeof this._provider.signTransaction !== 'function') {
            throw new Error('Binance Wallet cannot sign the migration transaction');
        }
        return this._provider.signTransaction(transaction);
    }
}

export const createMobileWalletAdapters = (network) => [
    new MobilePhantomWalletAdapter(),
    new MobileSolflareWalletAdapter({ network }),
    new BinanceWalletAdapter(),
    createWalletConnectAdapter(network),
    new LedgerWalletAdapter(),
    new InAppBrowserWalletAdapter({
        name: 'Trust',
        url: 'https://trustwallet.com',
        icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDQ4IDQ4Ij48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHJ4PSIxMiIgZmlsbD0iIzMzNzVFQyIvPjxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0yNCA5bDEyIDd2MTAuMmMwIDcuNi00LjcgMTQuNy0xMiAxNy44LTcuMy0zLjEtMTItMTAuMi0xMi0xNy44VjE2bDEyLTdaIi8+PC9zdmc+',
        buildBrowseUrl: trustBrowseUrl
    }),
    new InAppBrowserWalletAdapter({
        name: 'Backpack',
        url: 'https://backpack.app',
        icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDQ4IDQ4Ij48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHJ4PSIxMiIgZmlsbD0iI0UxMzU2MCIvPjxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0xNSAxNmgxOHY1SDE1em0wIDhoMTh2MTVIMTV6Ii8+PC9zdmc+',
        buildBrowseUrl: backpackBrowseUrl
    })
].filter(Boolean);
